import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  X,
  Check,
  Image as ImageIcon,
  FileImage,
  Sparkles,
} from "lucide-react";
import { PortadaConfig, Estampado } from "./types";

interface PersonalizadorPortadaProps {
  estampadosDisponibles: Estampado[];
  permiteImagenPersonalizada: boolean;
  onSelectionChange: (config: PortadaConfig) => void;
  value?: PortadaConfig | null;
}

export const PersonalizadorPortada = ({
  estampadosDisponibles,
  permiteImagenPersonalizada,
  onSelectionChange,
  value,
}: PersonalizadorPortadaProps) => {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);

  const tipoActual =
    value?.tipo ||
    (estampadosDisponibles.length > 0 ? "estampado" : "imagen-personalizada");

  // Handler para seleccionar estampado
  const handleEstampadoSelect = (estampadoId: string) => {
    onSelectionChange({
      tipo: "estampado",
      estampado: estampadoId,
    });
  };

  // Handlers para imagen personalizada
  const handleImagenDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleImagenDragLeave = () => {
    setIsDragging(false);
  };

  const handleImagenDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      await procesarImagen(files[0]);
    }
  };

  const handleImagenFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (files && files[0]) {
      await procesarImagen(files[0]);
    }
  };

  const procesarImagen = async (file: File) => {
    // Validar tamaño (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Archivo muy grande",
        description: "La imagen no debe superar los 5MB",
        variant: "destructive",
      });
      return;
    }

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Formato inválido",
        description: "Solo se permiten archivos de imagen (JPG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Leer imagen como base64 y detectar orientación
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;

      const img = new Image();
      img.onload = () => {
        const orientacion: 'horizontal' | 'vertical' =
          img.naturalWidth > img.naturalHeight ? 'horizontal' : 'vertical';
        onSelectionChange({
          tipo: "imagen-personalizada",
          imagenPersonalizada: result,
          orientacion,
        });
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemoverImagen = () => {
    onSelectionChange({
      tipo: "estampado",
    });
  };

  return (
    <Card>
      <CardHeader className="p-3 md:p-4 pb-2 md:pb-3">
        <CardTitle className="font-playfair text-brown-dark flex items-center gap-2 text-sm md:text-base">
          🎨 Personaliza tu Portada
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 md:p-4 pt-0">
        <Tabs
          value={tipoActual}
          onValueChange={(tipo) => {
            if (tipo === "estampado" && estampadosDisponibles.length > 0) {
              onSelectionChange({
                tipo: "estampado",
                estampado: estampadosDisponibles[0].id,
              });
            } else if (tipo === "imagen-personalizada") {
              onSelectionChange({
                tipo: "imagen-personalizada",
              });
            }
          }}
        >
          <TabsList className="grid w-full grid-cols-2 mb-3 h-auto p-1 gap-1 bg-brown-light/10">
            <TabsTrigger
              value="estampado"
              disabled={estampadosDisponibles.length === 0}
              className="text-xs md:text-sm py-2 md:py-2.5 px-2 md:px-3 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-brown-dark data-[state=active]:shadow-lg transition-all duration-200 flex items-center justify-center gap-1.5 font-medium"
            >
              <FileImage className="h-3.5 w-3.5" />
              <span>Estampados</span>
            </TabsTrigger>
            <TabsTrigger
              value="imagen-personalizada"
              disabled={!permiteImagenPersonalizada}
              className="text-xs md:text-sm py-2 md:py-2.5 px-2 md:px-3 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-brown-dark data-[state=active]:shadow-lg transition-all duration-200 flex items-center justify-center gap-1.5 font-medium"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Mi Imagen</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab de Estampados para Portada */}
          <TabsContent value="estampado" className="space-y-2">
            <p className="text-xs text-brown-dark/70 flex items-center gap-1.5">
              <FileImage className="h-3.5 w-3.5 text-primary" />
              Elige el fondo para la portada
            </p>

            {estampadosDisponibles.length === 0 ? (
              <div className="text-center py-6 text-brown-light/60">
                <p className="text-xs">
                  No hay fondos disponibles
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-1.5">
                {estampadosDisponibles.map((estampado) => (
                  <button
                    key={estampado.id}
                    onClick={() => handleEstampadoSelect(estampado.id)}
                    className={`relative ${estampado.orientacion === 'horizontal' ? 'aspect-[4/3]' : 'aspect-[3/4]'} rounded-md overflow-hidden border-2 transition-all hover:scale-105 active:scale-95 touch-manipulation ${
                      value?.estampado === estampado.id
                        ? "border-brown-dark ring-2 ring-offset-1 ring-brown-dark"
                        : "border-gray-300"
                    }`}
                    title={estampado.name}
                    aria-label={`Seleccionar estampado ${estampado.name}`}
                    aria-pressed={value?.estampado === estampado.id}
                  >
                    <img
                      src={estampado.path}
                      alt={estampado.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300&h=300&fit=crop";
                      }}
                    />
                    {value?.estampado === estampado.id && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <Check className="h-4 w-4 text-brown-dark drop-shadow-lg" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {value?.estampado && (
              <p className="text-xs text-brown-light/70 text-center">
                {
                  estampadosDisponibles.find((e) => e.id === value.estampado)
                    ?.name
                }
              </p>
            )}
          </TabsContent>

          {/* Tab de Imagen Personalizada */}
          <TabsContent
            value="imagen-personalizada"
            className="space-y-2"
          >
            <p className="text-xs text-brown-dark/70 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Sube tu propia imagen para la portada
            </p>

            {!value?.imagenPersonalizada ? (
              <div
                onDragOver={handleImagenDragOver}
                onDragLeave={handleImagenDragLeave}
                onDrop={handleImagenDrop}
                className={`group border-2 border-dashed rounded-lg p-6 md:p-8 text-center transition-all duration-300 cursor-pointer ${
                  isDragging
                    ? "border-primary bg-gradient-to-br from-primary/15 to-primary/5 scale-[1.01] shadow-lg"
                    : "border-brown-light/40 hover:border-primary/60 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent hover:shadow-md"
                }`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImagenFileSelect}
                  className="hidden"
                  id="imagen-personalizada-input"
                />
                <label
                  htmlFor="imagen-personalizada-input"
                  className="cursor-pointer flex flex-col items-center gap-4 md:gap-5"
                >
                  <div
                    className={`rounded-full p-5 md:p-7 transition-all duration-300 ${
                      isDragging
                        ? "bg-primary/30 scale-110"
                        : "bg-primary/15 group-hover:bg-primary/25 group-hover:scale-105"
                    }`}
                  >
                    <Upload
                      className={`h-10 w-10 md:h-14 md:w-14 text-brown-dark transition-transform duration-300 ${
                        isDragging ? "scale-110" : "group-hover:scale-105"
                      }`}
                    />
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    <p className="text-base md:text-xl font-semibold text-brown-dark">
                      {isDragging
                        ? "¡Suelta tu imagen aquí!"
                        : "Arrastra tu imagen aquí"}
                    </p>
                    <p className="text-xs md:text-sm text-brown-light/80">
                      o{" "}
                      <span className="text-primary font-semibold underline decoration-2 underline-offset-2 group-hover:text-primary/80">
                        haz click para seleccionar
                      </span>
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-brown-light/20">
                      <ImageIcon className="h-3.5 w-3.5 text-brown-light/60" />
                      <p className="text-xs text-brown-light/70 font-medium">
                        JPG, PNG hasta 5MB
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            ) : (
              <div className="relative border-2 border-primary rounded-lg overflow-hidden">
                <img
                  src={value.imagenPersonalizada}
                  alt="Imagen personalizada"
                  className="w-full max-h-[300px] md:max-h-[400px] object-contain bg-brown-light/5"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoverImagen}
                  className="absolute top-2 right-2 md:top-4 md:right-4 shadow-lg touch-manipulation text-xs md:text-sm"
                >
                  <X className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Cambiar imagen</span>
                  <span className="sm:hidden">Cambiar</span>
                </Button>

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 md:p-4">
                  <div className="flex items-center gap-2 text-white">
                    <ImageIcon className="h-3 w-3 md:h-4 md:w-4" />
                    <p className="text-xs md:text-sm font-medium">
                      Imagen personalizada cargada
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
